//* 绘图库
import Konva from "konva";
//* 组件库
import type { UiTypes, UiTable2 } from "various-ui";
//* 组件属性
import type { ModuleProps, ModuleReference } from "../index";
import { useDrawingOption } from "./utils";
import { useOptions } from "./options";

export const useComposable = (define: ModuleProps) => {
    //* 配置项
    const options = useOptions();

    //* 响应式属性
    const refs = {
        loading: ref<boolean>(true), //* 内置加载器
        exist: ref<boolean>(true), //* 页面状态配置
        page: ref<number>(1),
        date: ref<string>(""),
        tags: ref<string[]>([]),
        data: ref<ModuleReference[]>([]),
        count: ref<number>(0),
        selectors: ref<UiTypes.candidate[]>([]),
        checkboxs: ref<{ name: string; value: string; number: number }[]>([]),
        statistics: ref<{ name: string; number: number }[]>([]),
        sort: reactive({ key: "", value: "" }),
    };

    //* 节点
    const nodes = {
        table: ref<InstanceType<typeof UiTable2>>(),
        canvas: ref<HTMLDivElement>(),
        container: ref<HTMLDivElement>(),
    };

    //* 静态属性
    const variable = {
        stage: shallowRef<Konva.Stage>(),
        offset: { name: 0, value: 0 },
        tweens: <{ [name: string]: Konva.Tween[] }>{},
        observer: shallowRef<ResizeObserver>(),
    };

    //* 函数列表
    const methods = {
        //* 排序函数
        sort: (data: { key: string; value: string }) => {
            //* 数据更新
            refs.sort.key = data?.key || "";
            refs.sort.value = data?.value || "";
            //* 数据获取
            methods.acquire(1);
        },

        //* 数据获取
        acquire: (number?: number) => {
            //* 数据更新
            refs.loading.value = true;
            refs.page.value = number || refs.page.value;

            //* 排序初始化
            let sort = refs.sort.value == "desc" ? "-" : "";
            if (refs.sort.key == "year") {
                sort += "pub_date";
            } else if (refs.sort.key == "jif") {
                sort += "impact_factor";
            }

            //* 接口请求
            api.frontend(`/api/related/references`, {
                error: () => methods.hidden(),
                method: "POST",
                data: {
                    id: define.id,
                    mode: define.mode,
                    page: refs.page.value,
                    date: refs.date.value,
                    tags: refs.tags.value,
                    sort: sort ? [sort] : [],
                },
                success: (response) => {
                    if (!response) {
                        methods.hidden();
                    } else {
                        refs.loading.value = false;
                        //* 数据更新
                        refs.count.value = response.count || 0;
                        refs.data.value = response.data || [];
                        refs.selectors.value = response.selectors;
                        refs.checkboxs.value = response.checkboxs;
                        refs.statistics.value = response.statistics;

                        //* 可视化绘图
                        methods.drawing();
                        //* 回到顶部
                        if (nodes.container.value && scrollTo && !number) {
                            scrollTo({ top: nodes.container.value.offsetTop + 454, behavior: "smooth" });
                        }
                    }
                },
            });
        },

        hidden: () => {
            refs.exist.value = false;
            refs.loading.value = false;
        },

        //* 绘图函数
        drawing: () => {
            //* 检测是否存在容器和observer是否已注册
            if (nodes.container.value && !variable.observer.value) {
                variable.observer.value = useResizeWObserver(nodes.container.value, (size) => {
                    //* 检测容器节点是否存在
                    if (!nodes.canvas.value) return;

                    //* 数据初始化
                    variable.tweens = {};
                    variable.offset.name = 0;
                    variable.offset.value = 0;
                    //* 舞台初始化
                    variable.stage.value?.width(0);
                    if (variable.stage.value) {
                        variable.stage.value.removeChildren();
                        variable.stage.value.width(nodes.canvas.value.clientWidth);
                    } else {
                        variable.stage.value = new Konva.Stage({ container: nodes.canvas.value, width: nodes.canvas.value.clientWidth, height: 344 });
                    }

                    //* 绘图配置处理
                    const option = useDrawingOption(refs.statistics.value, {
                        size: nodes.canvas.value.clientWidth,
                        rect: 32,
                        offset: 16,
                    });

                    //* 场景初始化
                    const canvas = new Konva.Layer();

                    //* 内容渲染
                    canvas.add(drawing.bar(option));
                    canvas.add(drawing.axis());

                    //* 舞台渲染
                    variable.stage.value.add(canvas);
                });
            }
        },

        //* 选择函数
        selector: () => {
            //* 数据更新
            refs.sort.key = "";
            refs.sort.value = "";
            refs.tags.value.splice(0, refs.tags.value.length);
            //* 重置表格排序状态
            nodes.table.value?.sort();
            //* 数据获取
            methods.acquire(1);
            //* 柱状图状态更新
            for (const i in variable.tweens) {
                if (refs.date.value != i) variable.tweens[i].forEach((tween) => tween.reverse());
                else {
                    variable.tweens[i].forEach((tween) => tween.play());
                }
            }
        },
    };

    //* 绘图函数
    const drawing = {
        //* 文本
        content: (name: "name" | "value", option: { x: number; y: number; text: string; fill: string }) => {
            //* 初始化文本节点
            const Text = new Konva.Text({ y: option.y, text: option.text, fill: option.fill, fontSize: 14, fontFamily: "sans-serif" });
            //* 初始化数据
            const size = Text.getTextWidth();
            const offsetX = option.x - size / 2;
            const boundary = nodes.canvas.value?.clientWidth || 0;
            //* 检测文本是否在边界内
            if (offsetX < 0 || offsetX <= variable.offset[name] || offsetX + size > boundary) return;
            else {
                variable.offset[name] = offsetX + size;
            }

            return Text.x(offsetX);
        },

        //* 坐标轴
        axis: () => {
            return new Konva.Rect({ x: 0, y: 309.5, width: nodes.canvas.value?.clientWidth, height: 1, fill: "#384053" });
        },

        //* 柱子
        bar: (option: { rect: number; ratio: number; spacing: number }) => {
            //* 柱子容器
            const Bars = new Konva.Group({ y: 70 });
            //* 循环绘制柱子
            refs.statistics.value.forEach((value, index) => {
                //* 数据初始化
                const color = value.number ? "#6A80C8" : "#F5F6F8";
                const result = value.number?.toString() || "";
                const height = value.number ? value.number * option.ratio : 48;
                const offset = {
                    x: option.rect * index + option.spacing * (index + 1) + 16,
                    y: 240 - height,
                };

                //* 内容初始化
                const Bar = new Konva.Group();
                const Rect = new Konva.Rect({ x: offset.x, y: offset.y, width: option.rect, height: height, fill: color });
                const Name = drawing.content("name", { x: offset.x + option.rect / 2, y: offset.y - 22, text: result, fill: "#6D747F" });
                const References = drawing.content("value", {
                    x: offset.x + option.rect / 2,
                    y: offset.y + height + 12,
                    text: value.name,
                    fill: value.number > 0 ? "#6D747F" : "#c7c7c7",
                });

                //* 内容渲染
                Bar.add(Rect);
                if (Name && value.number) Bar.add(Name);
                if (References) Bar.add(References);
                if (value.number) {
                    nextTick(() => {
                        //* 动画初始化
                        variable.tweens[value.name] = [new Konva.Tween({ node: Rect, fill: "#73B1FF", duration: 0.3 })];
                        if (References) {
                            variable.tweens[value.name].push(new Konva.Tween({ node: References, fill: "#73B1FF", duration: 0.3 }));
                        }

                        //* 入场事件
                        Bar.on("mousemove", () => {
                            //* 样式更新
                            variable.tweens[value.name].forEach((tween) => tween.play());
                            if (variable.stage.value) {
                                variable.stage.value.container().style.cursor = "pointer";
                            }
                        });

                        //* 离场事件
                        Bar.on("mouseout", (ev: any) => {
                            //* 样式更新
                            variable.tweens[value.name].forEach((tween) => tween.play());
                            if (variable.stage.value) {
                                variable.stage.value.container().style.cursor = "auto";
                                if (refs.date.value != value.name) {
                                    variable.tweens[value.name].forEach((tween) => tween.reverse());
                                }
                            }
                        });

                        //* 获取当前点击位置, 并记录
                        Bar.on("mousedown", () => {
                            //* 状态更新
                            refs.date.value = value.name;
                            //* 触发选择回调
                            methods.selector();
                        });
                    });
                }

                //* 内容渲染队列
                Bars.add(Bar);
            });

            return Bars;
        },
    };

    return { refs, nodes, methods, options };
};
